import CheckCircleOutline from "@mui/icons-material/CheckCircleOutlined";
import ErrorOutline from "@mui/icons-material/ErrorOutlined";
import { Box, Grid, Link, useTheme } from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import apiRoutes from "../../constants/api-routes";
import useAuthHeader from "../../hooks/useAuthHeader";
import { RequestCard, RequestMetadata } from "./styled/requests.styled";

type RuleSummary = {
	name?: string;
};

type RequestPayload = {
	rule?: RuleSummary;
	requester?: string;
	createdAt?: string;
	requestCode?: string;
};

export default function ManagerApprovalRequest() {
	const { action, requestId } = useParams();
	const authHeader = useAuthHeader();

	const [approved, setApproved] = useState(false);
	const [rejected, setRejected] = useState(false);
	const [requestError, setRequestError] = useState("");

	const [request, setRequest] = useState<RequestPayload>({});
	const [rule, setRule] = useState<RuleSummary>({});

	const theme = useTheme();

	const approveRequest = useCallback(() => {
		if (!requestId) return;
		axios
			.put(apiRoutes.requestUpdate + "/" + requestId + "/approve", {}, { headers: authHeader })
			.then((res) => {
				if (res.status === 200) {
					setApproved(true);
				} else {
					const msg =
						res.data && typeof res.data === "object" && "message" in res.data
							? String((res.data as { message?: unknown }).message)
							: "";
					setRequestError("Error approving request: " + msg);
				}
			})
			.catch((err: unknown) => {
				console.log(err);
				const msg =
					axios.isAxiosError(err) && err.response?.data && typeof err.response.data === "object"
						? String((err.response.data as { message?: unknown }).message ?? "")
						: "";
				setRequestError("Error approving request: " + (msg || String(err)));
			});
	}, [authHeader, requestId]);

	const rejectRequest = useCallback(() => {
		if (!requestId) return;
		axios
			.put(apiRoutes.requestUpdate + "/" + requestId + "/reject", {}, { headers: authHeader })
			.then((res) => {
				if (res.status === 200) {
					setRejected(true);
				} else {
					setRequestError("Error rejecting request");
				}
			})
			.catch((err: unknown) => {
				console.log(err);
				const msg =
					axios.isAxiosError(err) && err.response?.data && typeof err.response.data === "object"
						? String((err.response.data as { message?: unknown }).message ?? "")
						: "";
				setRequestError("Error rejecting request: " + (msg || String(err)));
			});
	}, [authHeader, requestId]);

	const initRequest = useCallback(() => {
		if (!requestId) return;
		axios
			.get(apiRoutes.getRequest + "/" + requestId, { headers: authHeader })
			.then((res) => {
				const data = res.data as RequestPayload;
				setRequest(data);
				setRule(data.rule ?? {});
				if (action === "approve") {
					approveRequest();
				} else if (action === "reject") {
					rejectRequest();
				}
			})
			.catch((err: unknown) => {
				setRequestError("Error retrieving request: " + String(err));
				console.log(err);
			});
	}, [action, approveRequest, authHeader, rejectRequest, requestId]);

	useEffect(() => {
		void initRequest();
	}, [initRequest]);

	const logoSrc = import.meta.env.VITE_VERSION
		? "https://coapp-public-assets.s3.amazonaws.com/CoApp-Logo-COLOR-RGB.png"
		: "/images/CoApp-Logo-COLOR-RGB.png";

	return (
		<React.Fragment>
			<RequestCard>
				<Grid container spacing={1} sx={{ justifyContent: "space-evenly" }}>
					<Grid size={{ xs: 10 }} sx={{ marginTop: "25px", paddingTop: "20px" }}>
						<Link href="/rules/library" sx={{ display: "inline-block", lineHeight: 0 }}>
							<img src={logoSrc} alt="CoApp Logo" style={{ height: "40px", width: "115.62px" }} />
						</Link>
					</Grid>
					{approved ? (
						<Grid size={{ xs: 10 }}>
							<CheckCircleOutline sx={{ color: theme.palette.secondary.main, height: "36.67px", width: "36.67px" }} />
							<h4>Request Approved</h4>
						</Grid>
					) : null}
					{rejected && !requestError ? (
						<Grid size={{ xs: 10 }}>
							<ErrorOutline sx={{ color: theme.palette.error.main, height: "36.67px", width: "36.67px" }} />
							<h3>Request Rejected</h3>
						</Grid>
					) : null}
					{requestError ? (
						<Grid size={{ xs: 10 }}>
							<h3>{requestError}</h3>
						</Grid>
					) : null}
					{(approved || rejected) && !requestError ? (
						<Grid size={{ xs: 10 }}>
							<RequestMetadata>
								{rule.name} Request from {request.requester} on {request.createdAt} successfully{" "}
								{approved ? "approved" : "rejected"}.
							</RequestMetadata>
						</Grid>
					) : null}
					<Grid size={{ xs: 10 }} sx={{ marginTop: "20px", padding: "15px" }}>
						<Box component="span">Request ID: {request.requestCode}</Box>
					</Grid>
				</Grid>
			</RequestCard>
		</React.Fragment>
	);
}
